import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ChevronDown, ChevronUp, Copy, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Switch } from '@/components/ui-kit/switch';
import { Textarea } from '@/components/ui-kit/textarea';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { FormDefinition, Question, Section } from '../../types/form-engine.types';
import {
  createQuestion,
  createSection,
  duplicateQuestion,
  moveItem,
  withPositions,
} from '../../utils/question-factory';
import { QuestionBuilderCard } from '../question-builder-card/question-builder-card';

export interface FormBuilderProps {
  value: FormDefinition;
  onChange: (value: FormDefinition) => void;
  isAnnualPromotionForm?: boolean;
}

type PendingDelete =
  | { kind: 'question'; sectionIndex: number | null; index: number }
  | { kind: 'section'; index: number };

const move = moveItem;

export const FormBuilder = ({ value, onChange, isAnnualPromotionForm }: FormBuilderProps) => {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const setQuestions = (questions: Question[]) =>
    onChange({ ...value, FormData: withPositions(questions) });
  const setSections = (sections: Section[]) =>
    onChange({
      ...value,
      Sections: withPositions(sections).map((s) => ({
        ...s,
        Questions: withPositions(s.Questions),
      })),
    });
  const setSectionQuestions = (sectionIndex: number, questions: Question[]) =>
    setSections(
      value.Sections.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              Questions: questions.map((q) => ({ ...q, SectionId: section.SectionId })),
            }
          : section
      )
    );

  const toggleSections = (enabled: boolean) => {
    if (enabled === value.HasSections) return;
    if (enabled) {
      const first = createSection(0);
      first.Questions = value.FormData.map((q) => ({ ...q, SectionId: first.SectionId }));
      onChange({ HasSections: true, FormData: [], Sections: [first] });
    } else {
      onChange({
        HasSections: false,
        FormData: value.Sections.flatMap((s) =>
          s.Questions.map((q) => ({ ...q, SectionId: undefined }))
        ),
        Sections: [],
      });
    }
  };

  const duplicateSection = (index: number) => {
    const original = value.Sections[index];
    const copy: Section = {
      ...original,
      SectionId: uuidv4(),
      Title: original.Title ? `${original.Title} (Copy)` : '',
      Questions: original.Questions.map((q) => ({ ...duplicateQuestion(q), SectionId: undefined })),
    };
    copy.Questions = copy.Questions.map((q) => ({ ...q, SectionId: copy.SectionId }));
    const next = [...value.Sections];
    next.splice(index + 1, 0, copy);
    setSections(next);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'section') {
      setSections(value.Sections.filter((_, i) => i !== pendingDelete.index));
    } else if (pendingDelete.sectionIndex === null) {
      setQuestions(value.FormData.filter((_, i) => i !== pendingDelete.index));
    } else {
      setSectionQuestions(
        pendingDelete.sectionIndex,
        value.Sections[pendingDelete.sectionIndex].Questions.filter(
          (_, i) => i !== pendingDelete.index
        )
      );
    }
    setPendingDelete(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <label className="flex items-center gap-3 text-sm">
        <Switch checked={value.HasSections} onCheckedChange={toggleSections} />
        <span>
          <span className="font-medium">{t('ENABLE_SECTIONS')}</span>
          <span className="block text-xs text-muted-foreground">{t('SECTIONS_ENABLED_HINT')}</span>
        </span>
      </label>

      {!value.HasSections && (
        <>
          {value.FormData.map((question, index) => (
            <QuestionBuilderCard
              key={question.ItemId}
              question={question}
              isAnnualPromotionForm={isAnnualPromotionForm}
              onChange={(next) =>
                setQuestions(value.FormData.map((q, i) => (i === index ? next : q)))
              }
              onMove={(direction) => setQuestions(move(value.FormData, index, direction))}
              onDuplicate={() => {
                const next = [...value.FormData];
                next.splice(index + 1, 0, duplicateQuestion(question));
                setQuestions(next);
              }}
              onDelete={() => setPendingDelete({ kind: 'question', sectionIndex: null, index })}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setQuestions([...value.FormData, createQuestion()])}
          >
            <Plus className="size-4" />
            {t('ADD_QUESTION')}
          </Button>
        </>
      )}

      {value.HasSections && (
        <>
          {value.Sections.map((section, sectionIndex) => (
            <Card key={section.SectionId} className="border-primary/30">
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 space-y-3">
                    <Input
                      placeholder={`${t('SECTION')} ${sectionIndex + 1}`}
                      value={section.Title}
                      onChange={(e) =>
                        setSections(
                          value.Sections.map((s, i) =>
                            i === sectionIndex ? { ...s, Title: e.target.value } : s
                          )
                        )
                      }
                    />
                    <Textarea
                      rows={2}
                      placeholder={t('QUESTION_DESCRIPTION')}
                      value={section.Description}
                      onChange={(e) =>
                        setSections(
                          value.Sections.map((s, i) =>
                            i === sectionIndex ? { ...s, Description: e.target.value } : s
                          )
                        )
                      }
                    />
                  </div>
                  <div className="flex shrink-0 justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSections(move(value.Sections, sectionIndex, -1))}
                    >
                      <ChevronUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSections(move(value.Sections, sectionIndex, 1))}
                    >
                      <ChevronDown className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => duplicateSection(sectionIndex)}
                    >
                      <Copy className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPendingDelete({ kind: 'section', index: sectionIndex })}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>

                {section.Questions.map((question, index) => (
                  <QuestionBuilderCard
                    key={question.ItemId}
                    question={question}
                    sections={value.Sections}
                    currentSectionId={section.SectionId}
                    isAnnualPromotionForm={isAnnualPromotionForm}
                    onChange={(next) =>
                      setSectionQuestions(
                        sectionIndex,
                        section.Questions.map((q, i) => (i === index ? next : q))
                      )
                    }
                    onMove={(direction) =>
                      setSectionQuestions(sectionIndex, move(section.Questions, index, direction))
                    }
                    onDuplicate={() => {
                      const next = [...section.Questions];
                      next.splice(index + 1, 0, duplicateQuestion(question));
                      setSectionQuestions(sectionIndex, next);
                    }}
                    onDelete={() => setPendingDelete({ kind: 'question', sectionIndex, index })}
                  />
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setSectionQuestions(sectionIndex, [...section.Questions, createQuestion()])
                  }
                >
                  <Plus className="size-4" />
                  {t('ADD_QUESTION')}
                </Button>
              </CardContent>
            </Card>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setSections([...value.Sections, createSection(value.Sections.length)])}
          >
            <Plus className="size-4" />
            {t('ADD_SECTION')}
          </Button>
        </>
      )}

      <ConfirmationModal
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('DELETE')}
        description={
          pendingDelete?.kind === 'section'
            ? t('SECTION_DELETE_WARNING')
            : t('QUESTION_DELETE_WARNING')
        }
        confirmText={t('DELETE')}
        cancelText={t('CANCEL')}
        onConfirm={confirmDelete}
      />
    </div>
  );
};
